// hooks/useServiceHDSCreate.js
import { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import ServiceHDSService from '../../../../services/ServiceDHSService';
import AuthService from '../../../../services/AuthService';

const useServiceHDSCreate = (onServiceCreated, onClose) => {
    // État du formulaire
    const [newService, setNewService] = useState({
        name: '',
        description: '',
        createdBy: getCurrentUser()
    });

    // États de chargement
    const [isCreating, setIsCreating] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // États pour les fichiers
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);

    // Fonction pour obtenir le username de l'utilisateur connecté
    function getCurrentUser() {
        const user = AuthService.getCurrentUser();
        return user?.username || 'Utilisateur inconnu';
    }

    // Gestion des changements de champs
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewService(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Validation du fichier
    const validateFile = (file) => {
        // Types autorisés (images uniquement)
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            toast.error(`Type de fichier non supporté. Types autorisés: JPEG, PNG, JPG, WEBP, GIF, SVG`);
            return false;
        }

        // Taille max: 10 Mo
        const maxSize = 10 * 1024 * 1024; // 10 Mo en octets
        if (file.size > maxSize) {
            toast.error(`Fichier trop volumineux. Maximum: 10 Mo (${(file.size / 1024 / 1024).toFixed(2)} Mo)`);
            return false;
        }

        return true;
    };

    // Sélection d'un fichier
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!validateFile(file)) {
            // Réinitialiser l'input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        setSelectedFile(file);

        // Créer un aperçu
        const reader = new FileReader();
        reader.onload = (e) => {
            setImagePreview(e.target.result);
        };
        reader.onerror = () => {
            toast.error('Erreur lors de la lecture du fichier');
        };
        reader.readAsDataURL(file);
    };

    // Suppression de l'image
    const handleRemoveImage = () => {
        setSelectedFile(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Soumission du formulaire
    const handleSubmit = async () => {
        // Validation du nom
        if (!newService.name || newService.name.trim() === '') {
            toast.error('Le nom du service est obligatoire');
            return;
        }

        try {
            setIsCreating(true);

            // Appel au service pour créer le service avec le fichier
            const serviceData = {
                name: newService.name.trim(),
                description: newService.description?.trim() || ''
                // Ne pas inclure createdBy - le backend le gère via l'authentification
            };

            console.log('Création du service avec les données:', serviceData);

            // Appel direct au backend avec le fichier
            await ServiceHDSService.createService(serviceData, selectedFile);

            toast.success('Service créé avec succès');

            // Callback de succès
            if (onServiceCreated) onServiceCreated();

            // Fermeture du modal
            if (onClose) onClose();

            // Réinitialisation du formulaire
            resetForm();

        } catch (error) {
            let errorMessage = 'Erreur lors de la création du service';

            if (error.response?.data?.message) {
                errorMessage += `: ${error.response.data.message}`;
            } else if (error.response?.data?.error) {
                errorMessage += `: ${error.response.data.error}`;
            } else if (error.message) {
                errorMessage += `: ${error.message}`;
            }

            toast.error(errorMessage);
            console.error('Erreur détaillée:', error);
        } finally {
            setIsCreating(false);
        }
    };

    // Réinitialisation du formulaire
    const resetForm = () => {
        setNewService({
            name: '',
            description: '',
            createdBy: getCurrentUser()
        });
        setSelectedFile(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Gestion de la fermeture du modal
    const handleClose = () => {
        if (!isCreating && !isUploading) {
            resetForm();
            if (onClose) onClose();
        }
    };

    // Gestion du clic sur le backdrop
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && !isCreating && !isUploading) {
            handleClose();
        }
    };

    return {
        // États
        newService,
        isCreating,
        isUploading,
        selectedFile,
        imagePreview,
        fileInputRef,

        // Handlers
        handleInputChange,
        handleFileSelect,
        handleRemoveImage,
        handleSubmit,
        handleClose,
        handleBackdropClick,

        // Utilitaires
        getCurrentUser,
        resetForm
    };
};

export default useServiceHDSCreate;